import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API } from 'src/config';

/**
 * Account audit trail from GET /v1/user/audit-logs.
 *
 * Guarded by JwtUserGuard on the backend - master users only. A group user
 * receives 401, which the V1 error handler treats as a real auth failure, so
 * the view must be mounted behind a masterOnly route.
 *
 * Distinct from EventLogStore, which reads device events from /v1/event.
 */
export interface AuditLogEntry {
  id: number;
  occurred_at: string;
  event_type: string;
  result: string;
  actor_type: string | null;
  actor_id: string | null;
  actor_login: string | null;
  owner_user_id: number | null;
  target_type: string | null;
  target_id: string | null;
  channel: string | null;
  ip_address: string | null;
  details: Record<string, unknown> | null;
}

interface AuditLogResponse {
  items: AuditLogEntry[];
  page: number;
  count: number;
  total: number;
  hasMore: boolean;
}

export const AUDIT_PAGE_SIZE = 50;

class AuditLogStoreClass {
  entries: AuditLogEntry[] = [];
  page = 1;
  total = 0;
  hasMore = false;
  loading = false;
  error: string | null = null;
  from: string | null = null;
  to: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / AUDIT_PAGE_SIZE));
  }

  async fetchPage(page: number): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    const params = new URLSearchParams({
      page: String(page),
      count: String(AUDIT_PAGE_SIZE),
    });
    if (this.from) params.set('from', this.from);
    if (this.to) params.set('to', this.to);

    try {
      const result = await EmfitAPI.get<AuditLogResponse>(
        `${STATUS_API}/v1/user/audit-logs?${params.toString()}`,
        { noCache: true }
      );
      runInAction(() => {
        this.entries = result?.items ?? [];
        this.page = result?.page ?? page;
        this.total = result?.total ?? 0;
        this.hasMore = result?.hasMore ?? false;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to load audit log';
        this.loading = false;
      });
      console.error('Failed to fetch audit log:', error);
    }
  }

  /** Apply a date filter and jump back to the first page. */
  async setRange(from: string | null, to: string | null): Promise<void> {
    runInAction(() => {
      this.from = from;
      this.to = to;
      this.page = 1;
    });
    await this.fetchPage(1);
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.entries = [];
    this.page = 1;
    this.total = 0;
    this.hasMore = false;
    this.loading = false;
    this.error = null;
    this.from = null;
    this.to = null;
  }
}

export const AuditLogStore = new AuditLogStoreClass();
